import { useMemo } from "react";
import { Chart, formatNumber, getColorFromCssVariable } from "ui";
import { ChartCard } from "./ChartCard";
import { formatChartLabel } from "./utils";
import { useDatadogQuery } from "./useDatadogQuery";

type TransactionsData = Record<
  string,
  { success_count: number; failed_count: number }
>;

export function TotalTransactionsChart({
  isSameDayRange,
}: {
  isSameDayRange: boolean;
}) {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<TransactionsData>({
      chartType: "TOTAL_TRANSACTIONS",
      delayMs: 600,
    });

  const { labels, successData, failedData } = useMemo(() => {
    const labels: string[] = [];
    const successData: number[] = [];
    const failedData: number[] = [];

    if (data) {
      Object.entries(data).forEach(([timestamp, entry]) => {
        labels.push(timestamp);
        successData.push(entry.success_count);
        failedData.push(entry.failed_count);
      });
    }

    return { labels, successData, failedData };
  }, [data]);

  const successColor = getColorFromCssVariable("--chart-categorical-2-subtler");
  const failedColor = getColorFromCssVariable("--chart-categorical-3-subtle");

  return (
    <ChartCard
      ref={ref}
      title="Total transactions"
      description="Number of all payment attempts."
      contentClassName="pr-0 pl-4"
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
    >
      <Chart
        type="bar"
        labels={labels}
        datasets={[
          {
            label: "SUCCESSFUL",
            data: successData,
            backgroundColor: successColor,
            borderColor: successColor,
            stack: "stack0",
          },
          {
            label: "FAILED",
            data: failedData,
            backgroundColor: failedColor,
            borderColor: failedColor,
            stack: "stack0",
          },
        ]}
        className="h-full"
      >
        <Chart.Legend variant="row" />
        <Chart.Canvas
          className="h-0 flex-1"
          formatYLabel={(value) =>
            formatNumber({ value: value, compact: true })
          }
          formatXLabel={(value) => formatChartLabel({ value, isSameDayRange })}
        />
        <Chart.Tooltip
          valueFormatter={(value) => formatNumber({ value })}
          labelFormatter={(value) =>
            formatChartLabel({ value, isSameDayRange })
          }
        />
      </Chart>
    </ChartCard>
  );
}
